import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Alert,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  LinearProgress,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';

// Types
interface HealthStatus {
  status: string;
  app: string;
  version: string;
  environment: string;
  features: {
    uk_compliance: boolean;
    open_banking: boolean;
    ai_insights: boolean;
    cross_domain: boolean;
    mtd_vat: boolean;
    companies_house: boolean;
  };
}

interface AIGatewayHealth {
  status: string;
  health_ratio: number;
  total_instances: number;
  healthy_instances: number;
  total_requests: number;
  error_rate: number;
  average_response_time_ms: number;
  timestamp: string;
}

interface BackupHealth {
  success: boolean;
  health_check: {
    overall_health: string;
    health_score: number;
    issues_found: number;
    health_issues: string[];
    last_check: string;
  };
}

interface AuditStatistics {
  success: boolean;
  statistics: {
    period_days: number;
    audit_events: {
      total: number;
      high_severity: number;
      daily_average: number;
    };
    security_events: {
      total: number;
      unresolved: number;
      resolution_rate: number;
    };
    compliance_health: {
      score: number;
      status: string;
    };
  };
}

const FinancialAppMonitoring: React.FC = () => {
  const [health, setHealth] = useState<HealthStatus | null>(null);
  const [aiGateway, setAiGateway] = useState<AIGatewayHealth | null>(null);
  const [backup, setBackup] = useState<BackupHealth | null>(null);
  const [audit, setAudit] = useState<AuditStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    fetchMonitoringData();

    // Auto-refresh every 30 seconds
    if (autoRefresh) {
      const interval = setInterval(fetchMonitoringData, 30000);
      return () => clearInterval(interval);
    }
  }, [autoRefresh]);

  const fetchMonitoringData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch main health
      const healthResponse = await fetch('/health');
      if (healthResponse.ok) {
        const healthData = await healthResponse.json();
        setHealth(healthData);
      } else {
        throw new Error('Failed to fetch system health');
      }

      setLastUpdate(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch monitoring data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'healthy':
      case 'excellent':
      case 'good':
        return 'success';
      case 'degraded':
      case 'fair':
        return 'warning';
      case 'unhealthy':
      case 'poor':
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'healthy':
      case 'excellent':
      case 'good':
        return <CheckCircleIcon color="success" />;
      case 'degraded':
      case 'fair':
        return <WarningIcon color="warning" />;
      case 'unhealthy':
      case 'poor':
      case 'error':
        return <ErrorIcon color="error" />;
      default:
        return <CheckCircleIcon color="disabled" />;
    }
  };

  if (loading && !health) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
        <Button onClick={fetchMonitoringData} sx={{ ml: 2 }}>
          Retry
        </Button>
      </Alert>
    );
  }

  if (!health) return null;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            System Monitoring
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {health.app} v{health.version} • Last updated: {lastUpdate.toLocaleTimeString()}
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          <Tooltip title={autoRefresh ? "Auto-refresh enabled" : "Auto-refresh disabled"}>
            <Chip
              label={autoRefresh ? "Auto-refresh: ON" : "Auto-refresh: OFF"}
              color={autoRefresh ? "success" : "default"}
              onClick={() => setAutoRefresh(!autoRefresh)}
              clickable
            />
          </Tooltip>
          <Tooltip title="Refresh now">
            <IconButton onClick={fetchMonitoringData} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* System Status Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    System Status
                  </Typography>
                  <Chip
                    label={health.status.toUpperCase()}
                    color={getStatusColor(health.status)}
                    size="small"
                  />
                </Box>
                {getStatusIcon(health.status)}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Environment
                  </Typography>
                  <Typography variant="h6">{health.environment}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Version
                  </Typography>
                  <Typography variant="h6">v{health.version}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Features Status */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Enabled Features
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1}>
            {Object.entries(health.features).map(([key, value]) => (
              <Chip
                key={key}
                label={key.replace(/_/g, ' ').toUpperCase()}
                color={value ? 'success' : 'default'}
                size="small"
                icon={value ? <CheckCircleIcon /> : undefined}
              />
            ))}
          </Box>
        </CardContent>
      </Card>

      {/* AI Gateway Details */}
      {aiGateway && (
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  AI Gateway Performance
                </Typography>
                <Box display="flex" flexDirection="column" gap={2}>
                  <Box>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography variant="body2" color="text.secondary">Health Ratio</Typography>
                      <Typography variant="body2">{(aiGateway.health_ratio * 100).toFixed(1)}%</Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={aiGateway.health_ratio * 100}
                      color={aiGateway.health_ratio >= 0.8 ? 'success' : aiGateway.health_ratio >= 0.5 ? 'warning' : 'error'}
                    />
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">Total Requests</Typography>
                    <Typography variant="body2">{aiGateway.total_requests.toLocaleString()}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">Error Rate</Typography>
                    <Typography variant="body2">{(aiGateway.error_rate * 100).toFixed(2)}%</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">Avg Response Time</Typography>
                    <Typography variant="body2">{aiGateway.average_response_time_ms.toFixed(0)}ms</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {backup && (
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Backup System Health
                  </Typography>
                  <Box display="flex" flexDirection="column" gap={2}>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Overall Health</Typography>
                      <Chip
                        label={backup.health_check.overall_health.toUpperCase()}
                        color={getStatusColor(backup.health_check.overall_health)}
                        size="small"
                      />
                    </Box>
                    <Box>
                      <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography variant="body2" color="text.secondary">Health Score</Typography>
                        <Typography variant="body2">{backup.health_check.health_score}/100</Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={backup.health_check.health_score}
                        color={backup.health_check.health_score >= 90 ? 'success' : backup.health_check.health_score >= 75 ? 'warning' : 'error'}
                      />
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Issues Found</Typography>
                      <Typography variant="body2">{backup.health_check.issues_found}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      )}

      {/* Audit Statistics */}
      {audit && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Audit & Security (Last 30 Days)
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Audit Events
                  </Typography>
                  <Typography variant="h4">{audit.statistics.audit_events.total.toLocaleString()}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {audit.statistics.audit_events.daily_average.toFixed(1)} per day
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Security Events
                  </Typography>
                  <Typography variant="h4">{audit.statistics.security_events.total}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {audit.statistics.security_events.resolution_rate.toFixed(1)}% resolved
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Compliance Score
                  </Typography>
                  <Typography variant="h4">{audit.statistics.compliance_health.score}/100</Typography>
                  <Chip
                    label={audit.statistics.compliance_health.status.replace(/_/g, ' ').toUpperCase()}
                    color={getStatusColor(audit.statistics.compliance_health.status)}
                    size="small"
                  />
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default FinancialAppMonitoring;

